import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Row, Col, Card, Button, Select, Checkbox, Divider, Typography, Space, Input, List, DatePicker, InputNumber, Popconfirm } from 'antd';
import L from 'leaflet';
import { MapContainer, TileLayer, Polyline, CircleMarker, Circle, Tooltip, Marker, Polygon, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './RouteBasedDemo.css';
import { showMessage } from '../../../App';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

const CENTER = [35.5175, -86.5804];
const USE_GOOGLE_EMBED = false;
const GOOGLE_EMBED_URL = 'https://maps.google.com/maps?q=United%20States%20of%20America&t=m&z=7&output=embed&iwloc=near';

function toRad(d) { return (d * Math.PI) / 180; }
function haversineMiles(a, b) {
  const R = 3958.8; // miles
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function interpolateEveryMile(points, mileStep = 1) {
  if (!points || points.length < 2) return [];
  const pings = [];
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];
    const segMiles = haversineMiles(
      { lat: start.lat, lng: start.lng },
      { lat: end.lat, lng: end.lng }
    );
    const steps = Math.floor(segMiles / mileStep);
    for (let s = 1; s <= steps; s++) {
      const t = (s * mileStep) / segMiles; // 0..1 along segment
      const lat = start.lat + (end.lat - start.lat) * t;
      const lng = start.lng + (end.lng - start.lng) * t;
      pings.push({ id: `ping-${i}-${s}`, name: 'Ping', lat, lng });
    }
  }
  return pings;
}

function totalRouteMiles(points) {
  if (!points || points.length < 2) return 0;
  let sum = 0;
  for (let i = 0; i < points.length - 1; i++) {
    sum += haversineMiles(points[i], points[i + 1]);
  }
  return sum;
}

const RouteBasedDemo = () => {
  const { currentUser, token } = useAuth();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const [waypoints, setWaypoints] = useState([]);
  const [pingsEnabled, setPingsEnabled] = useState(false);
  const [geofencesOn, setGeofencesOn] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [mileStep, setMileStep] = useState(1);
  const [radiusMeters, setRadiusMeters] = useState(250);
  const [sequenceOpen, setSequenceOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [fetchedAddresses, setFetchedAddresses] = useState([]);
  const [selectedAddressKeys, setSelectedAddressKeys] = useState([]);
  const [showFetchedOnMap, setShowFetchedOnMap] = useState(true);
  const [mapRef, setMapRef] = useState(null);
  const [tileFailed, setTileFailed] = useState(false);
  const [geotabDateRange, setGeotabDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [stopDurationMin, setStopDurationMin] = useState(0);
  const [stopDurationMax, setStopDurationMax] = useState(null);
  const [geotabDbOptions, setGeotabDbOptions] = useState([]);
  const [selectedDatabases, setSelectedDatabases] = useState([]);
  const [fetchingAddresses, setFetchingAddresses] = useState(false);
  const [maxAddresses, setMaxAddresses] = useState(50);
  const [routeName, setRouteName] = useState('');
  const [routeType, setRouteType] = useState('Delivery');
  const [routeDateRange, setRouteDateRange] = useState([null, null]);
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRoute, setEditRoute] = useState(null);
  const [currentRouteName, setCurrentRouteName] = useState('');
  const [searching, setSearching] = useState(false);
  const [embedUrl, setEmbedUrl] = useState(GOOGLE_EMBED_URL);
  const [drawMode, setDrawMode] = useState('off');
  const [routePoints, setRoutePoints] = useState([]);
  const [areaPoints, setAreaPoints] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const providers = useMemo(() => ({
    osm: {
      id: 'osm',
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '© OpenStreetMap contributors'
    },
    esri: {
      id: 'esri',
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
      attribution: 'Tiles © Esri — Source: Esri, DeLorme, NAVTEQ'
    }
  }), []);
  const fallbackOrder = ['osm', 'esri'];
  const [selectedBasemap, setSelectedBasemap] = useState('osm');

  const redPinIcon = useMemo(() => L.divIcon({
    className: 'pm-pin-red',
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="42" viewBox="0 0 24 24" fill="none" stroke="none"><path d="M12 2C8.134 2 5 5.134 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7z" fill="#ef4444"/><circle cx="12" cy="9" r="3.2" fill="#ffffff"/></svg>`,
    iconSize: [28, 42],
    iconAnchor: [14, 42]
  }), []);

  const bluePinIcon = useMemo(() => L.divIcon({
    className: 'pm-pin-blue',
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="42" viewBox="0 0 24 24" fill="none" stroke="none"><path d="M12 2C8.134 2 5 5.134 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7z" fill="#3b82f6"/><circle cx="12" cy="9" r="3.2" fill="#ffffff"/></svg>`,
    iconSize: [28, 42],
    iconAnchor: [14, 42]
  }), []);

  // Click-to-add drawing handler component
  const MapClickCatcher = () => {
    useMapEvents({
      click(e) {
        const { latlng } = e;
        if (drawMode === 'route') {
          setRoutePoints(prev => [...prev, { lat: latlng.lat, lng: latlng.lng }]);
        } else if (drawMode === 'area') {
          setAreaPoints(prev => [...prev, { lat: latlng.lat, lng: latlng.lng }]);
        }
      }
    });
    return null;
  };

  // Auto-load selected saved route onto the map
  useEffect(() => {
    if (!selectedRouteId) return;
    if (String(selectedRouteId).startsWith('saved:')) {
      handleLoadRoute();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRouteId]);

  const clearDrawn = () => { setRoutePoints([]); setAreaPoints([]); };

  const saveDrawnRoute = async () => {
    if (!routePoints.length) { showMessage('warning', 'Add route points first.'); return; }
    if (!routeName || !routeName.trim()) { showMessage('warning', 'Please enter a route name.'); return; }
    const body = {
      route_name: routeName.trim(),
      type: routeType,
      waypoints: routePoints.map((p, idx) => ({ id: idx + 1, name: `Point ${idx + 1}`, lat: p.lat, lng: p.lng })),
      start_date: routeDateRange?.[0] ? routeDateRange[0].startOf('day').toISOString() : null,
      end_date: routeDateRange?.[1] ? routeDateRange[1].endOf('day').toISOString() : null,
    };
    try {
      await axios.post('/api/routes', body, { headers: authHeader() });
      showMessage('success', 'Route saved');
      fetchRoutes();
    } catch (e) {
      showMessage('error', e?.response?.data?.error || 'Failed to save route');
    }
  };

  useEffect(() => {
    if (!mapRef) return;
    const invalidate = () => {
      try { mapRef.invalidateSize(); } catch {}
    };
    // initial
    setTimeout(invalidate, 0);
    // on resize
    window.addEventListener('resize', invalidate);
    return () => window.removeEventListener('resize', invalidate);
  }, [mapRef]);

  const pings = useMemo(() => (pingsEnabled ? interpolateEveryMile(waypoints, mileStep) : []), [pingsEnabled, waypoints, mileStep]);

  const polylinePositions = useMemo(() => waypoints.map(w => [w.lat, w.lng]), [waypoints]);

  const totalMiles = useMemo(() => totalRouteMiles(waypoints), [waypoints]);

  const authHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  const fetchGeotabAccounts = async () => {
    try {
      const { data } = await axios.get('/api/integrations/geotab/accounts', { headers: authHeader() });
      const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
      let opts = items
        .map(a => a?.database)
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .map(db => ({ value: db, label: db }));
      const availableDatabases = opts.map(o => o.value);
      const email = currentUser?.email?.toLowerCase?.() || '';
      const role = (currentUser?.role || '').toLowerCase();
      const isAdmin = role === 'admin' || role === 'superadmin';
      if (!isAdmin) {
        if (email === 'pvrentals@pathmarkr.com') {
          opts = opts.filter(o => o.value === 'pvrentals' && availableDatabases.includes('pvrentals'));
        } else if (email === 'homelytics@pathmarkr.com') {
          opts = opts.filter(o => o.value === 'homelytics' && availableDatabases.includes('homelytics'));
        } else {
          opts = opts.filter(o => o.value !== 'pvrentals' && o.value !== 'homelytics');
        }
      }
      setGeotabDbOptions(opts);
      setSelectedDatabases(opts.map(o => o.value));
    } catch (e) {
      setGeotabDbOptions([]);
      setSelectedDatabases([]);
    }
  };

  useEffect(() => {
    fetchGeotabAccounts();
    fetchRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadRoute = () => {
    setLoaded(false);
    setPingsEnabled(false);
    setGeofencesOn(false);
    let usedName = '';
    let wps = [];
    if (!String(selectedRouteId || '').startsWith('saved:')) {
      showMessage('warning', 'Select a route to load.');
      return;
    }
    const rid = String(selectedRouteId).slice(6);
    const r = routes.find(rt => String(rt.id) === String(rid));
    if (!r) {
      showMessage('error', 'Selected route not found.');
      return;
    }
    usedName = r.route_name || r.routeName || 'Route';
    const wp = r.waypoints;
    wps = Array.isArray(wp) ? wp : (typeof wp === 'string' ? (()=>{ try { return JSON.parse(wp); } catch { return []; } })() : []);
    setWaypoints(wps);
    // Clear any drawings and search overlays so only the selected route is shown
    setRoutePoints([]);
    setAreaPoints([]);
    setSearchResults([]);
    setLoaded(true);
    setCurrentRouteName(usedName);
    setFetchedAddresses([]);
    setShowFetchedOnMap(false);
    setSelectedAddressKeys([]);
    showMessage('success', `Loaded ${usedName} (${wps.length} waypoint(s)).`);
    try {
      if (mapRef && wps.length > 0) {
        const bounds = L.latLngBounds(wps.map(w => [w.lat, w.lng]));
        mapRef.fitBounds(bounds, { padding: [40, 40] });
        mapRef.invalidateSize();
      }
    } catch {}
  };

  const handleTogglePings = (e) => {
    const v = e.target.checked;
    setPingsEnabled(v);
  };

  const handleAddressSearch = async (value) => {
    const q = String(value || '').trim();
    if (!q) return;
    if (USE_GOOGLE_EMBED) {
      const url = `https://maps.google.com/maps?q=${encodeURIComponent(q)}&t=m&z=14&output=embed&iwloc=near`;
      setEmbedUrl(url);
      return;
    }
    try {
      setSearching(true);
      const params = new URLSearchParams({ q });
      const { data } = await axios.get(`/api/address-search?${params.toString()}`, { headers: authHeader() });
      const items = (Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : [])).map((r, idx) => {
        const lat = Number(r?.lat ?? r?.latitude);
        const lng = Number(r?.lon ?? r?.lng ?? r?.longitude);
        return { key: r.key || `sr_${idx}`, label: r.display_name || r.label || q, lat, lng };
      }).filter(r => Number.isFinite(r.lat) && Number.isFinite(r.lng));
      setSearchResults(items);
      if (mapRef && items.length > 0) {
        if (items.length === 1) {
          try { mapRef.setView([items[0].lat, items[0].lng], 14); } catch {}
        } else {
          try {
            const bounds = L.latLngBounds(items.map(i => [i.lat, i.lng]));
            mapRef.fitBounds(bounds, { padding: [40, 40] });
          } catch {}
        }
      } else if (items.length === 0) {
        showMessage('warning', 'No results');
      }
    } catch (e) {
      showMessage('error', 'Address not found');
    } finally {
      setSearching(false);
    }
  };

  const fetchRoutes = async () => {
    try {
      setRoutesLoading(true);
      const { data } = await axios.get('/api/routes?limit=100', { headers: authHeader() });
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setRoutes(items);
    } catch {
      setRoutes([]);
    } finally {
      setRoutesLoading(false);
    }
  };

  const handleDeleteRoute = async (id) => {
    if (!id) return;
    try {
      await axios.delete(`/api/routes/${id}`, { headers: authHeader() });
      showMessage('success', 'Route deleted');
      fetchRoutes();
    } catch (e) {
      showMessage('error', e?.response?.data?.error || 'Failed to delete route');
    }
  };

  const openEditRoute = (route) => {
    if (!route) return;
    setEditRoute({
      id: route.id,
      route_name: route.route_name || route.routeName || '',
      type: route.type || 'Delivery',
      range: [route.start_date ? dayjs(route.start_date) : null, route.end_date ? dayjs(route.end_date) : null],
    });
    setEditOpen(true);
  };

  const saveEditRoute = async () => {
    if (!editRoute?.id) { setEditOpen(false); return; }
    try {
      const body = {
        route_name: editRoute.route_name,
        type: editRoute.type,
        start_date: editRoute.range?.[0] ? editRoute.range[0].startOf('day').toISOString() : null,
        end_date: editRoute.range?.[1] ? editRoute.range[1].endOf('day').toISOString() : null,
      };
      await axios.put(`/api/routes/${editRoute.id}`, body, { headers: authHeader() });
      showMessage('success', 'Route updated');
      setEditOpen(false);
      setEditRoute(null);
      fetchRoutes();
    } catch (e) {
      showMessage('error', e?.response?.data?.error || 'Failed to update route');
    }
  };

  const handleGenerateGeofences = () => {
    if (!loaded || waypoints.length === 0) {
      showMessage('warning', 'Load the route first.');
      return;
    }
    setGeofencesOn(true);
    const count = waypoints.length + (pingsEnabled ? pings.length : 0);
    showMessage('success', `Generated ${count} geofences.`);
  };

  const handleReset = () => {
    setWaypoints([]);
    setPingsEnabled(false);
    setGeofencesOn(false);
    setLoaded(false);
    setFetchedAddresses([]);
    setSelectedAddressKeys([]);
    try { if (mapRef) { mapRef.setView(CENTER, 12); } } catch {}
  };

  const useRouteInCampaigns = () => {
    if (!loaded || waypoints.length === 0) {
      showMessage('warning', 'Load a route first.');
      return;
    }
    try {
      const payload = {
        source: 'route-builder',
        name: currentRouteName || 'Route',
        waypoints,
        radiusMeters,
        pingsEnabled,
        mileStep,
      };
      sessionStorage.setItem('pm_campaign_import_route', JSON.stringify(payload));
      navigate('/client/campaigns');
    } catch (e) {
      showMessage('error', 'Failed to prepare campaign');
    }
  };

  const handleGetAddresses = () => {
    if (!Array.isArray(geotabDbOptions) || geotabDbOptions.length === 0) {
      showMessage('warning', 'Contact Justin Watkins Team');
      return;
    }
    const query = new URLSearchParams();
    query.set('limit', '100');
    if (typeof maxAddresses === 'number' && maxAddresses > 0) {
      query.set('maxAddresses', String(maxAddresses));
    }
    if (Array.isArray(selectedDatabases) && selectedDatabases.length > 0) {
      query.set('databases', selectedDatabases.join(','));
    } else {
      query.set('all', 'true');
    }
    if (Array.isArray(geotabDateRange) && geotabDateRange[0] && geotabDateRange[1]) {
      query.set('startDate', geotabDateRange[0].startOf('day').toISOString());
      query.set('endDate', geotabDateRange[1].endOf('day').toISOString());
    }
    if (typeof stopDurationMin === 'number' && stopDurationMin >= 0) {
      query.set('minStopMinutes', String(Math.floor(stopDurationMin)));
    }
    if (typeof stopDurationMax === 'number' && stopDurationMax >= 0) {
      query.set('maxStopMinutes', String(Math.floor(stopDurationMax)));
    }
    setFetchingAddresses(true);
    axios.get(`/api/integrations/geotab/gps-addresses?${query.toString()}`, { headers: authHeader() })
      .then((response) => {
        if (response.data && response.data.addresses) {
          const items = response.data.addresses.map((addr, index) => {
            const stopStart = addr.stopStart ? dayjs(addr.stopStart) : (addr.dateTime ? dayjs(addr.dateTime) : null);
            const stopEnd = addr.stopEnd ? dayjs(addr.stopEnd) : stopStart;
            const stopDurationMinutes = typeof addr.stopDurationMinutes === 'number' ? addr.stopDurationMinutes : null;
            const visitMoment = stopEnd || stopStart || (addr.dateTime ? dayjs(addr.dateTime) : null);
            const lat = Number(addr.latitude);
            const lng = Number(addr.longitude);
            return {
              key: addr.id || `geotab_${index}`,
              label: addr.address || `Location ${index + 1}`,
              address: addr.address,
              coords: (isFinite(lat) && isFinite(lng)) ? `${lat.toFixed(4)}, ${lng.toFixed(4)}` : '',
              lat,
              lng,
              stopStart,
              stopEnd,
              durationMin: stopDurationMinutes ?? 0,
              visitAt: visitMoment,
              device: addr.device,
              database: addr.database,
            };
          });
          setFetchedAddresses(items);
          setSelectedAddressKeys(items.map(i=>i.key));
          showMessage('success', `Fetched ${items.length} addresses from Geotab GPS`);
        }
      })
      .catch((error) => {
        showMessage('error', error?.response?.data?.error || 'Failed to fetch addresses from Geotab GPS');
      })
      .finally(() => {
        setFetchingAddresses(false);
      });
  };

  const toggleAddress = (key) => {
    setSelectedAddressKeys(prev => prev.includes(key) ? prev.filter(k=>k!==key) : [...prev, key]);
  };

  const selectAllAddresses = () => {
    setSelectedAddressKeys(fetchedAddresses.map(i=>i.key));
  };

  const clearSelectedAddresses = () => {
    setSelectedAddressKeys([]);
  };

  const handleUseSelected = () => {
    const selected = fetchedAddresses.filter(i => selectedAddressKeys.includes(i.key));
    showMessage('success', `Using ${selected.length} address(es) for campaign (demo).`);
  };

  return (
    <>
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>Route-Based Campaign</Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
          Simulated example of planned route targeting and breadcrumb coverage.
        </Typography.Paragraph>
      </div>

      <Row gutter={[16,16]}>
        {/* Map */}
        <Col xs={24} lg={16}>
          <div style={{ marginBottom: 8 }}>
            <Input.Search
              placeholder="Search address"
              enterButton="Search"
              onSearch={handleAddressSearch}
              loading={searching}
              allowClear
            />
            <Space style={{ marginTop: 8 }}>
              <Button type={drawMode==='route'?'primary':'default'} onClick={()=>setDrawMode(drawMode==='route'?'off':'route')}>Draw Route</Button>
              <Button type={drawMode==='area'?'primary':'default'} onClick={()=>setDrawMode(drawMode==='area'?'off':'area')}>Draw Area</Button>
              <Input
                size="small"
                placeholder="Route Name"
                value={routeName}
                onChange={(e)=>setRouteName(e.target.value)}
                style={{ width: 200 }}
              />
              <Button onClick={clearDrawn}>Clear</Button>
              <Button type="primary" onClick={saveDrawnRoute} disabled={routePoints.length===0 || !(routeName && routeName.trim())}>Save Drawn Route</Button>
            </Space>
          </div>
          <Card className="route-demo-card route-demo-map" bodyStyle={{ padding: 0, height: '100%' }} style={{ height: '70vh', position: 'relative' }}>
            {USE_GOOGLE_EMBED ? (
              <iframe
                title="Google Map"
                src={embedUrl}
                style={{ height: '100%', width: '100%', border: 0, display: 'block' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <MapContainer center={CENTER} zoom={12} whenCreated={setMapRef} style={{ height: '100%', width: '100%' }}>
                <MapClickCatcher />
                <TileLayer
                  key={selectedBasemap}
                  url={providers[selectedBasemap].url}
                  attribution={providers[selectedBasemap].attribution}
                  crossOrigin={true}
                  eventHandlers={{
                    tileerror: () => {
                      setTileFailed(true);
                      // fallback to next provider
                      const idx = fallbackOrder.indexOf(selectedBasemap);
                      const next = fallbackOrder[(idx + 1) % fallbackOrder.length];
                      if (next !== selectedBasemap) setSelectedBasemap(next);
                      setTimeout(() => { try { mapRef && mapRef.invalidateSize(); } catch {} }, 0);
                    }
                  }}
                />

                {routePoints.length > 0 && (
                  <>
                    <Polyline positions={routePoints.map(p=>[p.lat,p.lng])} pathOptions={{ color: '#7c3aed', weight: 4 }} />
                    {routePoints.map((p, idx) => (
                      <Marker
                        key={`rp-${idx}`}
                        position={[p.lat, p.lng]}
                        icon={redPinIcon}
                        draggable
                        eventHandlers={{
                          dragend: (e) => {
                            const ll = e.target.getLatLng();
                            setRoutePoints(prev => prev.map((pp,i)=> i===idx ? { lat: ll.lat, lng: ll.lng } : pp));
                          }
                        }}
                      />
                    ))}
                  </>
                )}

                {areaPoints.length > 2 && (
                  <>
                    <Polygon positions={areaPoints.map(p=>[p.lat,p.lng])} pathOptions={{ color: '#059669' }} />
                    {areaPoints.map((p, idx) => (
                      <Marker
                        key={`ap-${idx}`}
                        position={[p.lat, p.lng]}
                        icon={bluePinIcon}
                        draggable
                        eventHandlers={{
                          dragend: (e) => {
                            const ll = e.target.getLatLng();
                            setAreaPoints(prev => prev.map((pp,i)=> i===idx ? { lat: ll.lat, lng: ll.lng } : pp));
                          }
                        }}
                      />
                    ))}
                  </>
                )}

                {polylinePositions.length > 1 && (
                  <Polyline positions={polylinePositions} pathOptions={{ color: '#2563eb', weight: 4 }} />
                )}

                {/* Waypoints - blue */}
                {waypoints.map((w) => (
                  <CircleMarker key={w.id} center={[w.lat, w.lng]} radius={7} pathOptions={{ color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 1 }}>
                    <Tooltip>{w.name}</Tooltip>
                  </CircleMarker>
                ))}

                {/* Pings - light gray */}
                {pingsEnabled && pings.map((p) => (
                  <CircleMarker key={p.id} center={[p.lat, p.lng]} radius={4} pathOptions={{ color: '#cbd5e1', fillColor: '#e5e7eb', fillOpacity: 1 }} />
                ))}

                {/* Fetched Addresses - amber */}
                {showFetchedOnMap && fetchedAddresses.map((a) => (
                  drawMode === 'area' ? (
                    <Marker key={a.key} position={[a.lat, a.lng]} icon={bluePinIcon}>
                      <Tooltip>{a.label}</Tooltip>
                    </Marker>
                  ) : drawMode === 'route' ? (
                    <Marker key={a.key} position={[a.lat, a.lng]} icon={redPinIcon}>
                      <Tooltip>{a.label}</Tooltip>
                    </Marker>
                  ) : (
                    <CircleMarker key={a.key} center={[a.lat, a.lng]} radius={5} pathOptions={{ color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 1 }}>
                      <Tooltip>{a.label}</Tooltip>
                    </CircleMarker>
                  )
                ))}

                {/* Search Results - purple */}
                {searchResults.map((s) => (
                  drawMode === 'area' ? (
                    <Marker key={s.key} position={[s.lat, s.lng]} icon={bluePinIcon}>
                      <Tooltip>{s.label}</Tooltip>
                    </Marker>
                  ) : drawMode === 'route' ? (
                    <Marker key={s.key} position={[s.lat, s.lng]} icon={redPinIcon}>
                      <Tooltip>{s.label}</Tooltip>
                    </Marker>
                  ) : (
                    <CircleMarker key={s.key} center={[s.lat, s.lng]} radius={6} pathOptions={{ color: '#7c3aed', fillColor: '#7c3aed', fillOpacity: 1 }}>
                      <Tooltip>{s.label}</Tooltip>
                    </CircleMarker>
                  )
                ))}

                {/* Geofences - green translucent circles */}
                {geofencesOn && waypoints.map((w) => (
                  <Circle key={`gf-w-${w.id}`} center={[w.lat, w.lng]} radius={radiusMeters} pathOptions={{ color: '#22c55e' }} fillOpacity={0.15} />
                ))}
                {geofencesOn && pingsEnabled && pings.map((p) => (
                  <Circle key={`gf-p-${p.id}`} center={[p.lat, p.lng]} radius={radiusMeters} pathOptions={{ color: '#22c55e' }} fillOpacity={0.15} />
                ))}
              </MapContainer>
            )}

            {/* Legend */}
            <div className="route-demo-legend" style={{ position: 'absolute', bottom: 16, left: 16, padding: 8, fontSize: 12, lineHeight: 1.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 9999, background: '#3b82f6' }} /> Main Waypoints</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 9999, background: '#cbd5e1' }} /> Pings (Virtual Breadcrumbs)</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 9999, background: 'rgba(34,197,94,.6)' }} /> Geofence Area</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 9999, background: '#f59e0b' }} /> Fetched Addresses</div>
            </div>
          </Card>
          <Card className="route-demo-card" style={{ marginTop: 16 }} title={<Typography.Text strong>Saved Routes</Typography.Text>}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {routesLoading ? (
                <Typography.Text>Loading...</Typography.Text>
              ) : routes.length === 0 ? (
                <Typography.Text type="secondary">No routes yet</Typography.Text>
              ) : (
                <List
                  size="small"
                  bordered
                  dataSource={routes}
                  renderItem={(r) => (
                    <List.Item
                      actions={[
                        <Button size="small" onClick={()=>{ setSelectedRouteId(`saved:${r.id}`); handleLoadRoute(); }}>Load</Button>,
                        <Button size="small" onClick={()=>openEditRoute(r)}>Edit</Button>,
                        <Popconfirm title="Delete this route?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={()=>handleDeleteRoute(r.id)}>
                          <Button size="small" danger>Delete</Button>
                        </Popconfirm>
                      ]}
                    >
                      <Space direction="vertical" size={0}>
                        <Typography.Text strong>{r.route_name || r.routeName || 'Untitled Route'}</Typography.Text>
                        <Typography.Text type="secondary">
                          {r.type || '—'}{(r.start_date||r.end_date) ? ` • ${r.start_date ? dayjs(r.start_date).format('M/D/YYYY') : ''} - ${r.end_date ? dayjs(r.end_date).format('M/D/YYYY') : ''}` : ''}
                        </Typography.Text>
                      </Space>
                    </List.Item>
                  )}
                />
              )}
              <Button onClick={fetchRoutes}>Refresh</Button>
            </Space>
          </Card>
        </Col>

        {/* Controls */}
        <Col xs={24} lg={8}>
          <Card
            className="route-demo-card"
            title={<Typography.Text strong>Route Controls</Typography.Text>}
            extra={<Button onClick={()=>setCreateOpen(true)}>Create New Route</Button>}
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Button type="primary" disabled={!loaded || waypoints.length===0} onClick={useRouteInCampaigns}>
                Use This Route for Campaigns
              </Button>
              <div>
                <Typography.Text type="secondary">Fetch Routes from GeoTab</Typography.Text>
                <div style={{ marginTop: 6 }}>
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {geotabDbOptions.length > 0 && (
                      <div>
                        <Typography.Text strong>Select Geotab databases</Typography.Text>
                        <Select
                          mode="multiple"
                          allowClear
                          style={{ width: '100%', marginTop: 6 }}
                          value={selectedDatabases}
                          onChange={setSelectedDatabases}
                          options={geotabDbOptions}
                          getPopupContainer={(trigger)=>trigger.parentNode}
                          placeholder="Databases"
                        />
                      </div>
                    )}
                    {geotabDbOptions.length === 0 && (
                      <Typography.Text type="secondary">No Geotab credentials available for this user</Typography.Text>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                      <div>
                        <Typography.Text>Date range:</Typography.Text>
                        <div style={{ marginTop: 4 }}>
                          <DatePicker.RangePicker
                            value={geotabDateRange}
                            onChange={(values) => {
                              if (!values || values.length !== 2) {
                                setGeotabDateRange([dayjs().subtract(7, 'day'), dayjs()]);
                                return;
                              }
                              const [start, end] = values;
                              if (!start || !end) {
                                setGeotabDateRange([dayjs().subtract(7, 'day'), dayjs()]);
                              } else {
                                setGeotabDateRange([start, end]);
                              }
                            }}
                            allowClear={false}
                          />
                        </div>
                      </div>
                      <div>
                        <Typography.Text>Stop duration (minutes):</Typography.Text>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
                          <InputNumber
                            min={0}
                            placeholder="Min"
                            value={stopDurationMin}
                            onChange={(value) => {
                              setStopDurationMin(typeof value === 'number' ? value : 0);
                              if (typeof stopDurationMax === 'number' && value != null && value > stopDurationMax) {
                                setStopDurationMax(value);
                              }
                            }}
                            style={{ width: '100%' }}
                          />
                          <InputNumber
                            min={0}
                            placeholder="Max"
                            value={stopDurationMax}
                            onChange={(value) => {
                              if (value == null) {
                                setStopDurationMax(null);
                                return;
                              }
                              if (typeof value === 'number' && value >= 0) {
                                setStopDurationMax(value);
                                if (typeof stopDurationMin === 'number' && value < stopDurationMin) {
                                  setStopDurationMin(value);
                                }
                              }
                            }}
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography.Text>Max addresses:</Typography.Text>
                      <Space size={4}>
                        <Button size="small" type={maxAddresses===25? 'primary':'default'} onClick={()=>setMaxAddresses(25)}>25</Button>
                        <Button size="small" type={maxAddresses===50? 'primary':'default'} onClick={()=>setMaxAddresses(50)}>50</Button>
                        <Button size="small" type={maxAddresses===75? 'primary':'default'} onClick={()=>setMaxAddresses(75)}>75</Button>
                        <Button size="small" type={typeof maxAddresses!=='number'? 'primary':'default'} onClick={()=>setMaxAddresses(null)}>All</Button>
                      </Space>
                    </div>
                    <Button onClick={handleGetAddresses} loading={fetchingAddresses} disabled={geotabDbOptions.length === 0}>Get Addresses</Button>

                    {fetchedAddresses.length > 0 && (
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Space>
                          <Button size="small" onClick={selectAllAddresses}>Select All</Button>
                          <Button size="small" onClick={clearSelectedAddresses}>Clear</Button>
                          <Button size="small" type="primary" disabled={selectedAddressKeys.length===0} onClick={handleUseSelected}>Use Selected</Button>
                        </Space>
                        <Checkbox checked={showFetchedOnMap} onChange={(e)=>setShowFetchedOnMap(e.target.checked)}>Show on map</Checkbox>
                        <List
                          size="small"
                          bordered
                          header={<div>Fetched Addresses</div>}
                          dataSource={fetchedAddresses}
                          renderItem={(item) => (
                            <List.Item onClick={()=>toggleAddress(item.key)} style={{ cursor: 'pointer' }}>
                              <Space align="start" size={8}>
                                <Checkbox checked={selectedAddressKeys.includes(item.key)} onChange={()=>toggleAddress(item.key)} />
                                <Space direction="vertical" size={0}>
                                  <Typography.Text strong>{item.label}</Typography.Text>
                                  <Typography.Text type="secondary">{item.address}</Typography.Text>
                                  <Typography.Text type="secondary">{item.coords}</Typography.Text>
                                </Space>
                              </Space>
                            </List.Item>
                          )}
                        />
                      </Space>
                    )}
                  </Space>
                </div>
                {routes.length > 0 && (
                  <Select
                    style={{ width: '100%', marginTop: 6 }}
                    value={selectedRouteId}
                    onChange={setSelectedRouteId}
                    getPopupContainer={(trigger)=>trigger.parentNode}
                    options={routes.map(r => ({ value: `saved:${r.id}`, label: r.route_name || r.routeName || 'Route' }))}
                    placeholder="Select Route"
                  />
                )}
              </div>

              <Button type="primary" onClick={handleLoadRoute}>Load Route</Button>

              <Space align="center" wrap>
                <Typography.Text type="secondary">Basemap</Typography.Text>
                <Select
                  size="small"
                  style={{ width: 160 }}
                  value={selectedBasemap}
                  onChange={(v)=>{ setSelectedBasemap(v); setTileFailed(false); setTimeout(()=>{ try { mapRef && mapRef.invalidateSize(); } catch {} }, 0); }}
                  getPopupContainer={(trigger)=>trigger.parentNode}
                  options={[
                    { value: 'osm', label: 'OpenStreetMap' },
                    { value: 'esri', label: 'Esri Streets' }
                  ]}
                />
                {tileFailed && <Typography.Text type="danger">Tile error, switched provider</Typography.Text>}
              </Space>

              <Space align="center" wrap>
                <Checkbox checked={pingsEnabled} onChange={handleTogglePings} disabled={!loaded}>Add Pings</Checkbox>
                <Typography.Text type="secondary">Every</Typography.Text>
                <Select
                  size="small"
                  style={{ width: 100 }}
                  value={mileStep}
                  onChange={(v)=>setMileStep(v)}
                  disabled={!loaded}
                  getPopupContainer={(trigger)=>trigger.parentNode}
                  options={[
                    { value: 0.5, label: '0.5 mile' },
                    { value: 1, label: '1 mile' },
                    { value: 1.5, label: '1.5 miles' },
                    { value: 2, label: '2 miles' }
                  ]}
                />
                <Typography.Text type="secondary">Preset</Typography.Text>
                <Select
                  size="small"
                  style={{ width: 130 }}
                  value={mileStep}
                  onChange={(v)=>setMileStep(v)}
                  disabled={!loaded}
                  getPopupContainer={(trigger)=>trigger.parentNode}
                  options={[
                    { value: 2, label: 'Sparse (2 mi)' },
                    { value: 1, label: 'Normal (1 mi)' },
                    { value: 0.5, label: 'Dense (0.5 mi)' }
                  ]}
                />
              </Space>

              <Space align="center" wrap>
                <Typography.Text strong>Geofence Radius:</Typography.Text>
                <Select
                  style={{ width: 140 }}
                  value={radiusMeters}
                  onChange={setRadiusMeters}
                  disabled={!loaded}
                  getPopupContainer={(trigger)=>trigger.parentNode}
                  options={[{ value: 100, label: '100 m' }, { value: 250, label: '250 m' }, { value: 500, label: '500 m' }]}
                />
              </Space>

              <Space>
                <Button type="primary" disabled={!loaded} onClick={handleGenerateGeofences}>Generate Geofences</Button>
                <Button onClick={handleReset}>Reset Map</Button>
              </Space>

              <Space>
                <Button onClick={()=>setSequenceOpen(true)} disabled={!loaded}>View Waypoint Sequence</Button>
                <Button type="primary" ghost onClick={()=>setPublishOpen(true)} disabled={!geofencesOn}>Publish Campaign</Button>
              </Space>

              <Divider style={{ margin: '8px 0' }} />
              <Typography.Title level={5} style={{ marginBottom: 4 }}>Flow Guide</Typography.Title>
              <div style={{ maxHeight: 220, overflow: 'auto' }}>
                <ol style={{ paddingLeft: 18, margin: 0 }}>
                  <li><strong>Route Creation:</strong> Create or import a route (addresses, Geotab, AudienceLab, CSV, Google/RouteXL/MapQuest). Preview and save.</li>
                  <li><strong>Optional Pings:</strong> Enable breadcrumbs to add virtual points every 1–2 miles.</li>
                  <li><strong>Campaign Creation:</strong> Choose Route-Based as Location Source in campaign setup.</li>
                  <li><strong>Loading a Route:</strong> Select saved route and load waypoints (+ pings). Map and sequence appear.</li>
                  <li><strong>Geofence Generation:</strong> Pick a radius (e.g., 100m/250m). Circles are generated for all points.</li>
                  <li><strong>Review and Launch:</strong> Confirm totals and publish.</li>
                  <li><strong>Data Flow:</strong> Routes/waypoints saved; pings virtual; geofences build campaign coverage.</li>
                  <li><strong>Key Advantages:</strong> Predictive, reusable, flexible, accurate, expanded coverage.</li>
                  <li><strong>Example:</strong> Import past Geotab trip → Save route → Load in campaign → Enable 1-mile pings → Launch.</li>
                </ol>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </Space>

    <Modal
      title="Create New Route"
      open={createOpen}
      onOk={async ()=>{
        const body = {
          route_name: routeName && routeName.trim() ? routeName.trim() : 'Untitled Route',
          type: routeType,
          waypoints: waypoints && waypoints.length ? waypoints : [],
          start_date: routeDateRange?.[0] ? routeDateRange[0].startOf('day').toISOString() : null,
          end_date: routeDateRange?.[1] ? routeDateRange[1].endOf('day').toISOString() : null,
        };
        try {
          await axios.post('/api/routes', body, { headers: authHeader() });
          showMessage('success', 'Route saved');
          setCreateOpen(false);
          setRouteName('');
          setRouteType('Delivery');
          setRouteDateRange([null, null]);
          fetchRoutes();
        } catch (e) {
          showMessage('error', e?.response?.data?.error || 'Failed to save route');
        }
      }}
      onCancel={()=>setCreateOpen(false)}
      okText="Save"
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%', color: 'var(--ant-color-text, inherit)' }}>
        <Input placeholder="Route Name" value={routeName} onChange={(e)=>setRouteName(e.target.value)} />
        <Select
          style={{ width: '100%' }}
          value={routeType}
          onChange={setRouteType}
          options={[
            { value: 'Delivery', label: 'Delivery' },
            { value: 'Sales', label: 'Sales' },
            { value: 'Service', label: 'Service' },
            { value: 'Custom', label: 'Custom' }
          ]}
        />
        <div>
          <Typography.Text style={{ display: 'block', marginBottom: 4 }}>Route Dates</Typography.Text>
          <DatePicker.RangePicker
            value={routeDateRange}
            onChange={(vals)=>{
              if (!vals || vals.length !== 2) { setRouteDateRange([null,null]); return; }
              const [s,e] = vals; setRouteDateRange([s||null, e||null]);
            }}
            allowClear
            style={{ width: '100%' }}
          />
        </div>
      </Space>
    </Modal>

    <Modal
      title="Edit Route"
      open={editOpen}
      onOk={saveEditRoute}
      onCancel={()=>{ setEditOpen(false); setEditRoute(null); }}
      okText="Save"
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Input placeholder="Route Name" value={editRoute?.route_name || ''} onChange={(e)=>setEditRoute(prev=>({...prev, route_name: e.target.value}))} />
        <Select
          style={{ width: '100%' }}
          value={editRoute?.type || 'Delivery'}
          onChange={(v)=>setEditRoute(prev=>({...prev, type: v}))}
          options={[
            { value: 'Delivery', label: 'Delivery' },
            { value: 'Sales', label: 'Sales' },
            { value: 'Service', label: 'Service' },
            { value: 'Custom', label: 'Custom' }
          ]}
        />
        <div>
          <Typography.Text style={{ display: 'block', marginBottom: 4 }}>Route Dates</Typography.Text>
          <DatePicker.RangePicker
            value={editRoute?.range || [null,null]}
            onChange={(vals)=>{
              if (!vals || vals.length !== 2) { setEditRoute(prev=>({...prev, range: [null,null]})); return; }
              const [s,e] = vals; setEditRoute(prev=>({...prev, range: [s||null, e||null]}));
            }}
            allowClear
            style={{ width: '100%' }}
          />
        </div>
      </Space>
    </Modal>

    {/* Modal: Waypoint Sequence */}
    <Modal
      title="Waypoint Sequence"
      open={sequenceOpen}
      onOk={()=>setSequenceOpen(false)}
      onCancel={()=>setSequenceOpen(false)}
      okText="Close"
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      <Space direction="vertical" size={8} style={{ width: '100%', maxHeight: 300, overflow: 'auto' }}>
        <ol style={{ paddingLeft: 18, margin: 0 }}>
          {waypoints.map((w) => (
            <li key={w.id}><strong>{w.name}</strong> — {w.lat.toFixed(4)}, {w.lng.toFixed(4)}</li>
          ))}
        </ol>
        {pingsEnabled && (
          <Typography.Text type="secondary">+ {pings.length} ping point(s) will be included between waypoints.</Typography.Text>
        )}
      </Space>
    </Modal>

      {/* Modal: Publish Campaign Review */}
      <Modal
        title="Review & Publish"
        open={publishOpen}
        onOk={()=>{ setPublishOpen(false); showMessage('success', 'Campaign published (demo).'); }}
        onCancel={()=>setPublishOpen(false)}
        okText="Publish"
      >
        <Space direction="vertical" size={8}>
          <Typography.Text><Typography.Text strong>Campaign Type:</Typography.Text> Geofence (Route-Based)</Typography.Text>
          <Typography.Text><Typography.Text strong>Route:</Typography.Text> {loaded ? (currentRouteName || 'Route') : '—'}</Typography.Text>
          <Typography.Text><Typography.Text strong>Waypoints:</Typography.Text> {waypoints.length}</Typography.Text>
          <Typography.Text><Typography.Text strong>Pings:</Typography.Text> {pingsEnabled ? pings.length : 0} {pingsEnabled ? `(every ${mileStep} mi)` : ''}</Typography.Text>
          <Typography.Text><Typography.Text strong>Geofence Radius:</Typography.Text> {radiusMeters} m</Typography.Text>
          <Typography.Text><Typography.Text strong>Approx. Distance:</Typography.Text> {loaded ? `${totalMiles.toFixed(1)} miles` : '—'}</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ margin: 0 }}>All values are simulated for demo purposes only.</Typography.Paragraph>
        </Space>
      </Modal>
    </>
  );
};

export default RouteBasedDemo;
